import { useContext, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../../utils/auth";
import {
  ChevronsUpDown,
  ExternalLink,
  FileText,
  LogOut,
  Moon,
  Shield,
  Sun,
  Trash2,
} from "lucide-react";
import { API_BASE } from "../../shared/Constants";
import { ThemeContext } from "../../contexts/ThemeContext";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

interface UserMenuProps {
  variant?: "sidebar" | "header";
  collapsed?: boolean;
}

function getInitials(name?: string | null) {
  if (!name) return "M";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

export default function UserMenu({ variant = "sidebar", collapsed = false }: UserMenuProps) {
  const { user, logout, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { isDarkMode, toggleDarkMode } = useContext(ThemeContext);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const fullName =
    user?.given_name && user?.family_name
      ? `${user.given_name} ${user.family_name}`
      : user?.name ?? "Usuário Maritaca";
  const email = user?.email ?? "";
  const initials = getInitials(fullName);

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(`${API_BASE}/api/delete-account`, {
        method: "DELETE",
        headers: {
          Authorization: `Auth ${access_token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.status === 200) {
        logout({ logoutParams: { returnTo: window.location.origin + "/auth" } });
      } else {
        alert("Erro ao apagar conta. Tente novamente mais tarde.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {variant === "sidebar" ? (
            <button
              aria-label={`Conta de ${fullName}`}
              className="essential-account-button group flex w-full items-center gap-2.5 rounded-md text-left transition-colors hover:bg-background/60"
            >
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarImage src={user?.picture} alt={fullName} />
                <AvatarFallback className="bg-muted text-muted-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
                  <div className="essential-account-label min-w-0 flex-1" aria-hidden={collapsed}>
                    <p className="truncate text-[13px] font-semibold text-foreground">{fullName}</p>
                    <p className="truncate text-[11px] text-muted-foreground">{email}</p>
                  </div>
                  <ChevronsUpDown className="essential-account-chevron h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          ) : (
            <button
              aria-label={`Conta de ${fullName}`}
              className="flex items-center gap-2 rounded-md border border-border bg-background p-1 pl-1 pr-2 transition-colors hover:bg-muted"
            >
              <Avatar className="h-6 w-6">
                <AvatarImage src={user?.picture} alt={fullName} />
                <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
              </Avatar>
              <span className="text-xs font-medium text-foreground">
                {(user?.given_name as string) || fullName.split(" ")[0]}
              </span>
              <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={variant === "sidebar" ? "start" : "end"}
          side={variant === "sidebar" ? "top" : "bottom"}
          sideOffset={variant === "sidebar" ? 8 : 6}
          className="w-60"
        >
          {variant === "header" && (
            <>
              <DropdownMenuLabel className="!normal-case !tracking-normal">
                <div className="flex items-center gap-2.5">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={fullName} />
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-semibold text-foreground">{fullName}</p>
                    <p className="truncate text-[11px] font-normal text-muted-foreground">{email}</p>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={toggleDarkMode}>
            {isDarkMode ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            {isDarkMode ? "Modo claro" : "Modo escuro"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() =>
              window.open("https://www.maritaca.ai/termos-e-condicoes", "_blank", "noopener,noreferrer")
            }
          >
            <FileText className="h-3.5 w-3.5" />
            Termos e condições
            <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              window.open(
                "https://www.maritaca.ai/politica-de-privacidade",
                "_blank",
                "noopener,noreferrer"
              )
            }
          >
            <Shield className="h-3.5 w-3.5" />
            Política de privacidade
            <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setOpenDelete(true)}
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Excluir minha conta
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin + "/auth" } })}
          >
            <LogOut className="h-3.5 w-3.5" />
            Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={openDelete} onOpenChange={(o) => !o && setOpenDelete(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Conta</DialogTitle>
            <DialogDescription>
              Ao excluir sua conta, todas as chaves de API, histórico e créditos serão{" "}
              <strong className="text-foreground">permanentemente removidos</strong>. Esta ação
              não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 rounded-md border border-border bg-muted/40 p-3">
            <Trash2 className="mt-0.5 h-4 w-4 text-destructive" />
            <p className="text-sm text-foreground/80">
              Confirma que quer excluir <strong>{fullName}</strong> e todos os dados associados?
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpenDelete(false)} disabled={isDeleting} className="flex-1">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteAccount} disabled={isDeleting} className="flex-1">
              {isDeleting ? "Excluindo..." : "Excluir Conta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
